import {DataProvider, GetListParams, GetOneParams, CreateParams, UpdateParams, DeleteParams} from 'react-admin';
import {apiClient} from './apiClient';

// Resource mapping for admin endpoints
const RESOURCE_MAPPING: Record<string, string> = {
    'users': 'users',
    'media': 'media',
    'products': 'products',
    'brands': 'brands',
    'categories': 'categories',
    'tags': 'tags',
    'attribute-groups': 'attribute-groups',
    'attributes': 'attributes',
    'characteristic-groups': 'characteristic-groups',
    'characteristics': 'characteristics',
    'collections': 'collections',
    'vacancy-categories': 'vacancy-categories',
    'vacancies': 'vacancies',
    'vacancy-page-data': 'vacancy-page-data',
    'contact-groups': 'contact-groups',
    'contacts': 'contacts',
    'case-categories': 'case-categories',
    'cases': 'cases',
    'privacy-sections': 'privacy-sections',
    'privacy-section-items': 'privacy-section-items',
    'financing': 'financing',
    'about': 'about',
};

// Helper function to get the actual resource name for API calls
const getResourceName = (resource: string): string => {
    return RESOURCE_MAPPING[resource] || resource;
};

// Helper function to normalize API response
const normalizeResponse = (response: any) => {
    // Handle different response formats
    if (response.data && Array.isArray(response.data)) {
        return {
            data: response.data,
            total: response.meta?.total || response.count || response.data.length,
        };
    }

    if (Array.isArray(response)) {
        return {
            data: response,
            total: response.length,
        };
    }

    // Handle legacy formats
    if (response.users) {
        return {
            data: response.users,
            total: response.total || response.users.length,
        };
    }

    if (response.files) {
        return {
            data: response.files,
            total: response.meta?.total || response.files.length,
        };
    }

    return {
        data: response.data || response,
        total: response.meta?.total || response.count || 0,
    };
};

export const dataProvider: DataProvider = {
    getList: async (resource: string, params: GetListParams) => {
        const {page = 1, perPage = 10} = params.pagination || {};
        const {q, ...filters} = params.filter || {};

        try {
            const resourceName = getResourceName(resource);
            const response = await apiClient.getList(resourceName, {
                page,
                limit: perPage,
                search: q,
                ...filters,
            });

            return normalizeResponse(response);
        } catch (error: any) {
            console.error(`Failed to fetch ${resource}:`, error);
            throw new Error(error.response?.data?.message || `Failed to fetch ${resource}`);
        }
    },

    getOne: async (resource: string, params: GetOneParams) => {
        try {
            const resourceName = getResourceName(resource);
            const data = await apiClient.getOne(resourceName, params.id as string);
            return {data};
        } catch (error: any) {
            console.error(`Failed to fetch ${resource} with id ${params.id}:`, error);
            throw new Error(error.response?.data?.message || `Failed to fetch ${resource}`);
        }
    },

    getMany: async (resource: string, params: { ids: any[] }) => {
        try {
            const resourceName = getResourceName(resource);
            const promises = params.ids.map(id => apiClient.getOne(resourceName, id));
            const data = await Promise.all(promises);
            return {data};
        } catch (error: any) {
            console.error(`Failed to fetch multiple ${resource}:`, error);
            throw new Error(error.response?.data?.message || `Failed to fetch ${resource}`);
        }
    },

    getManyReference: async (resource: string, params: any) => {
        const {page = 1, perPage = 10} = params.pagination || {};
        const {target, id} = params;

        try {
            const resourceName = getResourceName(resource);
            const filters = {[target]: id, ...params.filter};

            const response = await apiClient.getList(resourceName, {
                page,
                limit: perPage,
                ...filters,
            });

            return normalizeResponse(response);
        } catch (error: any) {
            console.error(`Failed to fetch ${resource} references:`, error);
            throw new Error(error.response?.data?.message || `Failed to fetch ${resource}`);
        }
    },

    create: async (resource: string, params: CreateParams) => {
        try {
            const resourceName = getResourceName(resource);

            // Special handling for media uploads
            if (resource === 'media' && params.data.file) {
                const data = await apiClient.uploadFile(params.data.file);
                return {data};
            }

            const data = await apiClient.create(resourceName, params.data);
            return {data};
        } catch (error: any) {
            console.error(`Failed to create ${resource}:`, error);
            throw new Error(error.response?.data?.message || `Failed to create ${resource}`);
        }
    },

    update: async (resource: string, params: UpdateParams) => {
        try {
            const resourceName = getResourceName(resource);
            const data = await apiClient.update(resourceName, params.id as string, params.data);
            return {data};
        } catch (error: any) {
            console.error(`Failed to update ${resource}:`, error);
            throw new Error(error.response?.data?.message || `Failed to update ${resource}`);
        }
    },

    updateMany: async (resource: string, params: { ids: any[]; data: any }) => {
        try {
            const resourceName = getResourceName(resource);
            const promises = params.ids.map(id =>
                apiClient.update(resourceName, id, params.data)
            );
            await Promise.all(promises);
            return {data: params.ids};
        } catch (error: any) {
            console.error(`Failed to update multiple ${resource}:`, error);
            throw new Error(error.response?.data?.message || `Failed to update ${resource}`);
        }
    },

    delete: async (resource: string, params: DeleteParams) => {
        try {
            const resourceName = getResourceName(resource);
            await apiClient.delete(resourceName, params.id as string);
            return {data: params.previousData};
        } catch (error: any) {
            console.error(`Failed to delete ${resource}:`, error);
            throw new Error(error.response?.data?.message || `Failed to delete ${resource}`);
        }
    },

    deleteMany: async (resource: string, params: { ids: any[] }) => {
        try {
            const resourceName = getResourceName(resource);
            const promises = params.ids.map(id => apiClient.delete(resourceName, id));
            await Promise.all(promises);
            return {data: params.ids};
        } catch (error: any) {
            console.error(`Failed to delete multiple ${resource}:`, error);
            throw new Error(error.response?.data?.message || `Failed to delete ${resource}`);
        }
    },
};
